import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { flatten as _flatten } from 'lodash/fp'

import {
  catchError,
  endWith,
  filter,
  forkJoin,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs'

import {
  BulkDataModelAttribute,
  DataModelAttributeType,
} from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import {
  AiDesignerAPI,
  GenerateUiPayload,
  SaveDraftPayload,
} from '@flowx/designer/modules/new-ai/agents/designer/api/designer.api'
import { DesignerServiceNew } from '@flowx/designer/modules/new-ai/agents/designer/services/designer-new.service'
import { DesignerService } from '@flowx/designer/modules/new-ai/agents/designer/services/designer.service'
import { UiFlowDesignerService } from '@flowx/designer/modules/new-ai/agents/designer/services/ui-flow-designer.service'
import { FormFieldsWithCurrencyCode } from '@flowx/designer/modules/ui-designer/services/templateConfig.entity'
import { DataModelBuilderApiService } from '@flowx/designer/shared/api/data-model-builder.api'
import { EventHandlerApiService } from '@flowx/designer/shared/api/event-handler.api'
import { ReusableTemplatesApiService } from '@flowx/designer/shared/api/reusable-templates.api'
import { UiFlowsApiService } from '@flowx/designer/shared/api/ui-flows.api'
import { UpdateTemplateConfigKey } from '@flowx/designer/shared/entities/data-model.entity'
import { DataModelAutocompleteService } from '@flowx/designer/shared/services/data-model-autocomplete.service'
import { ProcessDataModelActions } from '@flowx/designer/shared/store/actions/process-data-model.actions'
import { TemplateConfigActions } from '@flowx/designer/shared/store/actions/template-config.actions'
import { UiFlowTemplateConfigActions } from '@flowx/designer/shared/store/actions/ui-flow-template-config.actions'
import {
  selectCurrentResourceId,
  selectDataModelEntitiesLeaves,
} from '@flowx/designer/shared/store/selectors/data-model.selectors'
import {
  selectReusableUiTemplateResourceId,
  selectSetAiUserTaskRootTemplateConfig,
} from '@flowx/designer/shared/store/selectors/template-config.selectors'
import {
  EventHandlerActionType,
  EventHandlerTriggerEvent,
} from '@flowx/designer/shared/types/event-handler.types'
import { FormFieldsEnum, TemplateConfigInterface } from '@flowx/shared/interfaces'

import { AiDesignerActions } from './designer.actions'

import { AiDesignerContext, DataModelDraft } from '../../../common/ai.dictionary'
// eslint-disable-next-line import/no-cycle
import { AiActions } from '../../../store/ai.actions'
import { AiSelectors } from '../../../store/ai.selectors'

@Injectable()
export class AiDesignerEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #designerApi = inject(AiDesignerAPI)
  #reusableTemplatesApi = inject(ReusableTemplatesApiService)
  #dataModelAutocompleteService = inject(DataModelAutocompleteService)
  #dataModelBuilderApiService = inject(DataModelBuilderApiService)
  #uiFlowsApiService = inject(UiFlowsApiService)
  #uiFlowActionApiService = inject(EventHandlerApiService)

  generateUiStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.generateUiStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.generatedUI),
        this.#store.select(AiSelectors.draftTemplateConfig),
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(selectDataModelEntitiesLeaves),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      map(
        ([{ prompt }, sessionId, generatedUI, , currentUI, dataModel, currentPlanStep]) =>
          ({
            question: prompt,
            session_id: sessionId,
            context:
              currentUI && currentUI.templateConfig && currentUI.templateConfig.length > 0
                ? currentUI
                : generatedUI,
            data_model: JSON.stringify(dataModel),
            _fromPlanner: !!currentPlanStep,
          }) satisfies GenerateUiPayload & { _fromPlanner: boolean }
      ),
      withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
      switchMap(([payload]) => {
        const formData = new FormData()
        formData.append('prompt', payload.question)
        formData.append('data_model', payload.data_model)
        formData.append('session_id', payload.session_id)
        if (payload.context) {
          formData.append('context', JSON.stringify(payload.context))
        }
        if (payload._fromPlanner) {
          formData.append('skip_greeting', 'true')
        }
        return this.#designerApi.getSSEGenerateUI(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, dataModel]) =>
            of(AiDesignerActions.getAnswerEventStream({ response, dataModel }))
          ),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  generateUiFlowStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.generateUiFlowStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(selectDataModelEntitiesLeaves)
      ),
      switchMap(([{ prompt }, sessionId, dataModel]) => {
        const formData = new FormData()
        formData.append('prompt', prompt)
        formData.append('data_model', JSON.stringify(dataModel))
        formData.append('session_id', sessionId ?? '')

        return this.#designerApi.getSSEGenerateUiFlow(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, currentDataModel]) => {
            // Forward each SSE chunk to the shared streaming handler so the
            // chat panel can show incremental output while the stream is live.
            const streamAction = AiDesignerActions.getAnswerEventStream({
              response,
              dataModel: currentDataModel,
            })

            // When the chunk carries the final answer (the "end" event from
            // the stream), extract the UI flow response and dispatch success.
            try {
              const msg = response.message
              const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg
              // The backend sends { end: { answer: { interface, components, reasoning } } }
              const uiFlowAnswer = parsed?.end?.answer ?? parsed?.answer ?? parsed
              if (uiFlowAnswer?.components?.length) {
                const uiFlowResponse = {
                  interface: uiFlowAnswer.interface ?? { it: '', ty: 'WB' as const, ds: '' },
                  components: uiFlowAnswer.components ?? [],
                  navigation: uiFlowAnswer.navigation ?? [],
                }
                return of(
                  streamAction,
                  AiDesignerActions.generateUiFlowStreamSuccess({ response: uiFlowResponse })
                )
              }
            } catch {
              // Not yet a complete JSON payload — normal during streaming
            }

            return of(streamAction)
          }),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  // ─── Phase 2: save UI flow pages and wire navigation actions ─────────────────

  keepUiFlowDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepUiFlowDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.draftTemplateConfig),
        this.#store.select(selectCurrentResourceId),
        this.#store.select(AiSelectors.selectGeneratedUINew)
      ),
      switchMap(([{ resourceId }, draftTemplateConfig, currentResourceId, generatedUINew]) => {
        if (!generatedUINew?.components?.length && !draftTemplateConfig) {
          return of(AiDesignerActions.keepUiFlowDraftSuccess())
        }

        const uiFlowResourceId = resourceId || currentResourceId
        const navigationActions = (generatedUINew as any)?.navigation || []

        // Strip fake IDs so the backend creates new entities
        const stripIds = (tc: any): any => {
          const { id, flowxUuid, ...rest } = tc
          return {
            ...rest,
            templateConfig: tc.templateConfig?.map(stripIds) || [],
          }
        }

        // Build ALL page trees from generatedUINew (not just the selected one)
        const components = generatedUINew?.components || []
        const pageRoots = components.filter((c: any) => c.pId === 0)

        let allPages: TemplateConfigInterface[]
        if (pageRoots.length > 1) {
          // Multi-page: map each page root to a TemplateConfigInterface tree
          allPages = pageRoots
            .map((pageRoot: any) => {
              // Collect this page's component subtree
              const pageComponents = new Set<number>([pageRoot.id])
              let changed = true
              while (changed) {
                changed = false
                for (const c of components) {
                  if (!pageComponents.has(c.id) && pageComponents.has(c.pId)) {
                    pageComponents.add(c.id)
                    changed = true
                  }
                }
              }
              const filteredConfig = {
                ...generatedUINew,
                components: components.filter((c: any) => pageComponents.has(c.id)),
              }
              return DesignerServiceNew.mapAiUiToTemplateConfig(
                () => Math.floor(Math.random() * Number.MAX_SAFE_INTEGER),
                () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`),
                filteredConfig
              ) as TemplateConfigInterface
            })
            .filter(Boolean)
        } else {
          // Single page: use draftTemplateConfig
          allPages = draftTemplateConfig ? [draftTemplateConfig] : []
        }

        if (!allPages.length) {
          return of(AiDesignerActions.keepUiFlowDraftSuccess())
        }

        // Save each page as a root template config
        const saveRequests = allPages.map((page) =>
          this.#uiFlowsApiService.createNodeTemplate(uiFlowResourceId, stripIds(page)).pipe(
            map((response) => response.resourceContent.template),
            catchError((err) => {
              console.error('[KEEP] Failed to save:', err)
              return of(null)
            })
          )
        )

        return forkJoin(saveRequests).pipe(
          switchMap((savedTrees) => {
            // If no navigation actions, we're done
            if (!navigationActions.length || !savedTrees.some(Boolean)) {
              return of(AiDesignerActions.keepUiFlowDraftSuccess())
            }

            // Build key → saved entity mappings from saved trees
            // The response from createNodeTemplate can be either:
            // - A nested tree (templateConfig: [{...}, {...}])
            // - A flat normalized map (templateConfig: { "123": {...}, "456": {...} })
            const keyToId = new Map<string, number>()
            const keyToFlowxUuid = new Map<string, string>()
            const collectFromNode = (node: any): void => {
              if (!node || typeof node !== 'object') {
                return
              }
              const k = node.key || node.customIdentifier
              if (k) {
                keyToId.set(k, node.id)
                if (node.flowxUuid) {
                  keyToFlowxUuid.set(k, node.flowxUuid)
                }
              }
              // Handle nested array of objects
              if (Array.isArray(node.templateConfig)) {
                node.templateConfig.forEach((child: any) => {
                  if (typeof child === 'object') {
                    collectFromNode(child)
                  }
                })
              }
              // Handle flat normalized map { "id": {...templateConfig} }
              if (node.templateConfig && !Array.isArray(node.templateConfig)) {
                Object.values(node.templateConfig).forEach((child: any) => {
                  if (typeof child === 'object') {
                    collectFromNode(child)
                  }
                })
              }
            }
            savedTrees.filter(Boolean).forEach(collectFromNode)

            console.log('[NAV] Navigation wiring:', {
              navigationActions: navigationActions.length,
              keysCollected: keyToId.size,
              uuidsCollected: keyToFlowxUuid.size,
              keys: [...keyToId.keys()],
            })

            // Wire navigation actions: for each nav action, add a NAVIGATE_TO uiAction
            const navRequests = navigationActions
              .map((navAction: any) => {
                const buttonId = keyToId.get(navAction.source_button_key)
                const targetFlowxUuid = keyToFlowxUuid.get(navAction.target_page_key)
                if (!buttonId || !targetFlowxUuid) {
                  return null
                }

                return this.#uiFlowActionApiService.addEventHandler(
                  buttonId,
                  {
                    name: '',
                    type: EventHandlerActionType.NAVIGATE_TO,
                    triggerEvent: EventHandlerTriggerEvent.CLICK,
                    params: {
                      destination: { uiTemplateFlowxUuid: targetFlowxUuid },
                    },
                  } as any,
                  undefined
                )
              })
              .filter(Boolean)

            if (!navRequests.length) {
              return of(AiDesignerActions.keepUiFlowDraftSuccess())
            }

            return forkJoin(navRequests).pipe(
              map(() => AiDesignerActions.keepUiFlowDraftSuccess()),
              catchError(() => of(AiDesignerActions.keepUiFlowDraftSuccess())) // Don't fail on nav errors
            )
          }),
          catchError((err) =>
            of(AiDesignerActions.keepUiFlowDraftFailure({ statusCode: err?.status, errorMsg: err }))
          )
        )
      })
    )
  )

  // After UI Flow keep success: reload template configs
  onKeepUiFlowDraftSuccess = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDesignerActions.keepUiFlowDraftSuccess),
        withLatestFrom(this.#store.select(selectCurrentResourceId)),
        tap(([, resourceId]) => {
          // Reload UI Flow template configs so the page dropdown updates
          if (resourceId) {
            this.#store.dispatch(UiFlowTemplateConfigActions.loadUIFlow({ id: resourceId }))
          }
        })
      ),
    { dispatch: false }
  )

  // ─── Phase 3: edit existing UI flow ──────────────────────────────────────────

  editUiFlowStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.editUiFlowStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.generatedUiFlowTemplateConfigs),
        this.#store.select(selectDataModelEntitiesLeaves)
      ),
      switchMap(([{ prompt }, sessionId, currentPages, dataModel]) => {
        const context = currentPages?.length
          ? UiFlowDesignerService.templateConfigToAiContext(currentPages)
          : null

        const formData = new FormData()
        formData.append('prompt', prompt)
        formData.append('data_model', JSON.stringify(dataModel))
        formData.append('session_id', sessionId ?? '')
        if (context) {
          formData.append('context', JSON.stringify(context))
        }

        return this.#designerApi.getSSEEditUiFlow(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, currentDataModel]) => {
            const streamAction = AiDesignerActions.getAnswerEventStream({
              response,
              dataModel: currentDataModel,
            })

            const messageStr: string = typeof response.message === 'string' ? response.message : ''
            try {
              const parsed = JSON.parse(messageStr)
              const uiFlowAnswer = parsed?.end?.answer ?? parsed
              if (uiFlowAnswer?.cs?.length) {
                const uiFlowResponse = {
                  interface: {
                    it: uiFlowAnswer.it ?? '',
                    ty: 'WB' as const,
                    ds: uiFlowAnswer.ds ?? '',
                  },
                  components: uiFlowAnswer.cs ?? [],
                  navigation: uiFlowAnswer.navigation ?? [],
                }
                return of(
                  streamAction,
                  AiDesignerActions.generateUiFlowStreamSuccess({ response: uiFlowResponse })
                )
              }
            } catch {
              // Not yet a complete JSON payload — normal during streaming
            }

            return of(streamAction)
          }),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  // ─── Phase 4: subtree edit ────────────────────────────────────────────────────

  subtreeEditUiFlowStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.subtreeEditUiFlowStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.generatedUiFlowTemplateConfigs),
        this.#store.select(selectDataModelEntitiesLeaves)
      ),
      switchMap(([{ prompt, selectedTemplateConfigId }, sessionId, currentPages, dataModel]) => {
        const subtreeContext = currentPages?.length
          ? UiFlowDesignerService.extractSubtreeAsAiContext(currentPages, selectedTemplateConfigId)
          : null

        const formData = new FormData()
        formData.append('prompt', prompt)
        formData.append('data_model', JSON.stringify(dataModel))
        formData.append('session_id', sessionId ?? '')
        if (subtreeContext) {
          formData.append('context', JSON.stringify(subtreeContext))
        }

        return this.#designerApi.getSSESubtreeEditUiFlow(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, currentDataModel]) => {
            const streamAction = AiDesignerActions.getAnswerEventStream({
              response,
              dataModel: currentDataModel,
            })

            const messageStr: string = typeof response.message === 'string' ? response.message : ''
            try {
              const parsed = JSON.parse(messageStr)
              const uiFlowAnswer = parsed?.end?.answer ?? parsed
              if (uiFlowAnswer?.cs?.length) {
                const editedSubtreeResponse = {
                  interface: {
                    it: uiFlowAnswer.it ?? '',
                    ty: 'WB' as const,
                    ds: uiFlowAnswer.ds ?? '',
                  },
                  components: uiFlowAnswer.cs ?? [],
                  navigation: uiFlowAnswer.navigation ?? [],
                }
                // Merge the edited subtree back into the full page tree:
                // dispatch generateUiFlowStreamSuccess so the reducer can store
                // the result and the keep/discard flow continues unchanged.
                return of(
                  streamAction,
                  AiDesignerActions.generateUiFlowStreamSuccess({ response: editedSubtreeResponse })
                )
              }
            } catch {
              // Not yet a complete JSON payload — normal during streaming
            }

            return of(streamAction)
          }),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  // ─── UI Template generation ──────────────────────────────────────────────────

  generateUiTemplateStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.generateUiTemplateStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(selectDataModelEntitiesLeaves),
        this.#store.select(AiSelectors.currentPlanStep)
      ),
      switchMap(([{ prompt }, sessionId, currentUI, dataModel, currentPlanStep]) => {
        const formData = new FormData()
        formData.append('prompt', prompt)
        formData.append('data_model', JSON.stringify(dataModel))
        formData.append('session_id', sessionId ?? '')
        if (currentUI && currentUI.templateConfig && currentUI.templateConfig.length > 0) {
          formData.append('context', JSON.stringify(currentUI))
        }
        if (currentPlanStep) {
          formData.append('skip_greeting', 'true')
        }
        return this.#designerApi.getSSEGenerateUiTemplate(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, currentDataModel]) =>
            of(AiDesignerActions.getAnswerEventStream({ response, dataModel: currentDataModel }))
          ),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  generateUiTemplateFromImagesStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.generateUiTemplateFromImagesStream),
      withLatestFrom(
        this.#store.select(selectDataModelEntitiesLeaves),
        this.#store.select(AiSelectors.sessionId)
      ),
      map(([{ imageFiles, prompt }, dataModel, sessionId]) =>
        this.#mapImagePromptToFormData(imageFiles[0], prompt, dataModel, sessionId)
      ),
      withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
      switchMap(([formData]) =>
        this.#designerApi.getSSEGenerateUiTemplate(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, dataModel]) =>
            of(AiDesignerActions.getAnswerEventStream({ response, dataModel }))
          ),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  editUiTemplateStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.editUiTemplateStream),
      withLatestFrom(
        this.#store.select(AiSelectors.sessionId),
        this.#store.select(AiSelectors.currentUI),
        this.#store.select(selectDataModelEntitiesLeaves)
      ),
      switchMap(([{ prompt }, sessionId, currentUI, dataModel]) => {
        const formData = new FormData()
        formData.append('prompt', prompt)
        formData.append('data_model', JSON.stringify(dataModel))
        formData.append('session_id', sessionId ?? '')
        if (currentUI) {
          formData.append('context', JSON.stringify(currentUI))
        }
        return this.#designerApi.getSSEEditUiTemplate(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, currentDataModel]) =>
            of(AiDesignerActions.getAnswerEventStream({ response, dataModel: currentDataModel }))
          ),
          catchError(({ status }) =>
            of(AiActions.getAnswerFailure({ statusCode: status, errorMsg: null }))
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      })
    )
  )

  // ─── Phase 5: connect a button to an integration workflow ──────────────────

  connectUiFlowToWorkflow = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.connectUiFlowToWorkflow),
      switchMap(({ buttonTemplateConfigId, workflowName }) => {
        const uiAction = {
          name: workflowName,
          type: EventHandlerActionType.START_PROCESS,
          triggerEvent: EventHandlerTriggerEvent.CLICK,
          params: { customKeys: [workflowName] },
        }

        return this.#uiFlowActionApiService
          .addEventHandler(buttonTemplateConfigId, uiAction, undefined)
          .pipe(
            map(() => AiDesignerActions.keepUiFlowDraftSuccess()),
            catchError(({ status, error }) =>
              of(AiDesignerActions.keepUiFlowDraftFailure({ statusCode: status, errorMsg: error }))
            )
          )
      })
    )
  )

  generateUiFromImagesStream = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.generateUiFromImagesStream),
      withLatestFrom(
        this.#store.select(selectDataModelEntitiesLeaves),
        this.#store.select(AiSelectors.sessionId)
      ),
      map(([{ imageFiles, prompt }, dataModel, sessionId]) =>
        this.#mapImagePromptToFormData(imageFiles[0], prompt, dataModel, sessionId)
      ),
      withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
      switchMap(([formData]) =>
        this.#designerApi.getSSEGenerateUI(formData).pipe(
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
          switchMap(([response, dataModel]) =>
            of(AiDesignerActions.getAnswerEventStream({ response, dataModel }))
          ),
          catchError(({ status }) =>
            of(
              AiActions.getAnswerFailure({
                statusCode: status,
                errorMsg: null,
              })
            )
          ),
          endWith(AiActions.getAnswerStreamCompleted())
        )
      )
    )
  )

  extractUiFromDocument = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.extractUiFromDocument),
      switchMap(({ documentContextReference }) =>
        this.#designerApi
          .extractUiFromDocumentNew(documentContextReference)
          .pipe(takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))))
      ),
      map((response) => {
        this.#store.dispatch(AiDesignerActions.extractUiFromDocumentSuccess({ response }))
        return AiDesignerActions.generateUiStream({ prompt: response.response })
      }),
      catchError(({ status, error }) =>
        of(AiDesignerActions.generateUiFailure({ statusCode: status, errorMsg: error }))
      )
    )
  )

  onKeepUiDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepUiDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedUI),
        this.#store.select(AiSelectors.generatedUINew),
        this.#store.select(AiSelectors.editDiffAppliedConfig)
      ),
      map(([, generatedUI, generatedUINew, editDiffAppliedConfig]) =>
        generatedUI || generatedUINew || editDiffAppliedConfig
          ? AiDesignerActions.keepTemplateConfigDraft()
          : AiDesignerActions.keepDataModelDraft()
      )
    )
  )

  keepTemplateConfigDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepTemplateConfigDraft),
      map(() => AiDesignerActions.saveTemplateConfigDraft())
    )
  )

  continueKeepActionEvents = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepDataModelDraftSuccess),
      withLatestFrom(this.#store.select(AiSelectors.currentUI)),
      switchMap(([, currentUI]) =>
        this.#designerApi
          .getSSEActions({
            continue_editing: true,
            action_type: 'keep_ui_generation',
            context: currentUI,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
            switchMap(([response, dataModel]) =>
              of(AiDesignerActions.getAnswerEventStream({ response, dataModel }))
            ),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      )
    )
  )

  continueDiscardActionEvents = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.discardUiDraftSuccess),
      withLatestFrom(this.#store.select(AiSelectors.currentUI)),
      switchMap(([, currentUI]) =>
        this.#designerApi
          .getSSEActions({
            continue_editing: true,
            action_type: 'discard_ui_generation',
            context: currentUI,
          })
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            withLatestFrom(this.#store.select(selectDataModelEntitiesLeaves)),
            switchMap(([response, dataModel]) =>
              of(AiDesignerActions.getAnswerEventStream({ response, dataModel }))
            ),
            catchError(({ status }) =>
              of(
                AiActions.getAnswerFailure({
                  statusCode: status,
                  errorMsg: null,
                })
              )
            ),
            endWith(AiActions.getAnswerStreamCompleted())
          )
      )
    )
  )

  saveTemplateConfigDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.saveTemplateConfigDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedUI),
        this.#store.select(AiSelectors.generatedUINew),

        this.#store.select(
          selectSetAiUserTaskRootTemplateConfig(AiSelectors.selectedDesignerUserTaskAreaId)
        ),
        this.#store.select(AiSelectors.draftTemplateConfig),
        this.#store.select(AiSelectors.selectedDesignerUserTaskAreaId),
        this.#store.select(AiSelectors.selectedDesignerTemplateConfigId),
        this.#store.select(AiSelectors.generatedLocationId),
        this.#store.select(AiSelectors.editDiffAppliedConfig),
        this.#store.select(AiSelectors.generatedEditDiff),
        this.#store.select(selectReusableUiTemplateResourceId)
      ),
      switchMap(
        ([
          ,
          generatedUI,
          generatedUINew,
          userTaskRootTemplateConfig,
          draftTemplateConfig,
          userTaskNavigationAreaId,
          selectedTemplateConfigId,
          generatedLocationId,
          editDiffAppliedConfig,
          generatedEditDiff,
          reusableTemplateResourceId,
        ]) => {
          // Reusable UI Template path: save via the reusable templates API
          if (reusableTemplateResourceId && draftTemplateConfig) {
            const stripTempFields = (node: TemplateConfigInterface): TemplateConfigInterface => {
              const clean = { ...node } as any
              delete clean.id
              delete clean.draft
              delete clean.meta
              if (clean.templateConfig?.length) {
                clean.templateConfig = clean.templateConfig.map(stripTempFields)
              }
              return clean
            }
            const templateToSave = stripTempFields(draftTemplateConfig)
            return this.#reusableTemplatesApi
              .createNodeTemplate(reusableTemplateResourceId, templateToSave)
              .pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                tap((response) => {
                  const savedTemplate = (response as any)?.resourceContent?.template
                  if (savedTemplate) {
                    this.#store.dispatch(
                      AiDesignerActions.mapTemplateConfigComponentsToDataModel({
                        templateConfig: [savedTemplate],
                      })
                    )
                  }
                }),
                map(() => AiDesignerActions.saveTemplateConfigDraftSuccess()),
                catchError(({ status, error }) =>
                  of(
                    AiDesignerActions.saveTemplateConfigDraftFailure({
                      statusCode: status,
                      errorMsg: error,
                    })
                  )
                )
              )
          }

          // Edit diff path: use dedicated save that handles add/edit/remove/move separately
          if (editDiffAppliedConfig && generatedEditDiff) {
            return this.#designerApi
              .saveEditDiff(userTaskNavigationAreaId, editDiffAppliedConfig, generatedEditDiff)
              .pipe(
                takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                tap((templateConfig) => {
                  this.#store.dispatch(
                    AiDesignerActions.mapTemplateConfigComponentsToDataModel({ templateConfig })
                  )
                }),
                map(() => AiDesignerActions.saveTemplateConfigDraftSuccess()),
                catchError(({ status, error }) =>
                  of(
                    AiDesignerActions.saveTemplateConfigDraftFailure({
                      statusCode: status,
                      errorMsg: error,
                    })
                  )
                )
              )
          }

          // Normal path: merge and bulk add
          const mergedConfig = DesignerService.mergeDraftTemplateConfig(
            draftTemplateConfig,
            userTaskRootTemplateConfig,
            selectedTemplateConfigId,
            generatedLocationId
          )

          const payload = {
            navAreaId: userTaskNavigationAreaId,
            draftConfig: {
              ...draftTemplateConfig,
              uiTemplateParentId: selectedTemplateConfigId || userTaskRootTemplateConfig?.id,
            },
            mergedConfig,
            aiContext: {
              answer: JSON.stringify(generatedUI || generatedUINew),
            } as AiDesignerContext,
          } satisfies SaveDraftPayload

          return this.#designerApi.saveDraft(payload).pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            tap((templateConfig) =>
              this.#store.dispatch(
                AiDesignerActions.mapTemplateConfigComponentsToDataModel({ templateConfig })
              )
            ),
            map(() => AiDesignerActions.saveTemplateConfigDraftSuccess()),
            catchError(({ status, error }) =>
              of(
                AiDesignerActions.saveTemplateConfigDraftFailure({
                  statusCode: status,
                  errorMsg: error,
                })
              )
            )
          )
        }
      )
    )
  )

  onSaveTemplateConfigDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.saveTemplateConfigDraftSuccess),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedDesignerUserTaskAreaId),
        this.#store.select(selectReusableUiTemplateResourceId)
      ),
      tap(([, areaId, reusableTemplateResourceId]) => {
        if (reusableTemplateResourceId) {
          this.#store.dispatch(
            TemplateConfigActions.loadReusableTemplate({
              id: reusableTemplateResourceId,
            })
          )
        } else {
          this.#store.dispatch(
            TemplateConfigActions.loadUserTaskTemplateConfigs({
              navigationAreaIds: [areaId],
            })
          )
        }
      }),
      switchMap(([, , reusableTemplateResourceId]) =>
        reusableTemplateResourceId
          ? this.#actions$.pipe(
              ofType(TemplateConfigActions.loadReusableTemplateSuccess),
              take(1),
              map(() => AiDesignerActions.keepTemplateConfigDraftSuccess())
            )
          : this.#actions$.pipe(
              ofType(TemplateConfigActions.loadUserTaskTemplateConfigsSuccess),
              take(1),
              map(() => AiDesignerActions.keepTemplateConfigDraftSuccess())
            )
      )
    )
  )

  keepDataModelDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.keepDataModelDraft),
      withLatestFrom(
        this.#store
          .select(AiSelectors.generatedUIDataModelChanges)
          .pipe(
            map(
              (generatedUIDataModelChanges) =>
                !!generatedUIDataModelChanges?.new?.find((dataModelItem) => !dataModelItem.inactive)
            )
          )
      ),
      map(([, hasNewActiveDataModelItems]) =>
        hasNewActiveDataModelItems
          ? AiDesignerActions.saveDataModelDraft()
          : AiDesignerActions.linkTemplateConfigKeys()
      )
    )
  )

  saveDataModelDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.saveDataModelDraft),
      withLatestFrom(this.#store.select(AiSelectors.generatedUIDataModelChanges)),
      map(([, dataModelChanges]) =>
        dataModelChanges.new
          .filter((dataModel) => !dataModel.inactive)
          .map(
            ({ key, type, label, multiInstance }) =>
              ({
                path: key,
                type,
                description: label,
                multiInstance,
              }) satisfies BulkDataModelAttribute
          )
      ),
      withLatestFrom(this.#store.select(selectCurrentResourceId)),
      switchMap(([bulkDataModelAttributes, processResourceId]) =>
        this.#dataModelBuilderApiService
          .bulkAddAttributesFromPath(processResourceId, bulkDataModelAttributes)
          .pipe(
            takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
            map(() => AiDesignerActions.saveDataModelDraftSuccess()),
            catchError(({ status, error }) =>
              of(
                AiDesignerActions.saveDataModelDraftFailure({
                  statusCode: status,
                  errorMsg: error,
                })
              )
            )
          )
      )
    )
  )

  onSaveDataModelDraftSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.saveDataModelDraftSuccess),
      map(() => AiDesignerActions.reloadDataModel())
    )
  )

  reloadDataModel = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.reloadDataModel),
      withLatestFrom(this.#store.select(selectCurrentResourceId)),
      tap(([, processResourceId]) => {
        this.#store.dispatch(
          ProcessDataModelActions.loadEntities({
            resourceId: processResourceId,
          })
        )
      }),
      switchMap(() =>
        this.#actions$.pipe(
          ofType(ProcessDataModelActions.loadEntitiesSuccess),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          take(1),
          map(() => AiDesignerActions.reloadDataModelSuccess())
        )
      )
    )
  )

  onReloadDataModelSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.reloadDataModelSuccess),
      map(() => AiDesignerActions.linkTemplateConfigKeys())
    )
  )

  linkTemplateConfigKeys = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.linkTemplateConfigKeys),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedDesignerUserTaskAreaId),
        this.#store.select(AiSelectors.generatedUIDataModelChanges)
      ),
      tap(([, areaId, generatedUIDataModelChanges]) => {
        const payload: UpdateTemplateConfigKey[] = [
          ...(generatedUIDataModelChanges?.new ?? []),
          ...(generatedUIDataModelChanges?.existing ?? []),
        ]
          .map((dataModel) => {
            const key =
              FormFieldsWithCurrencyCode.includes(
                dataModel.templateConfigIdentifier as FormFieldsEnum
              ) && dataModel.type === DataModelAttributeType.CURRENCY
                ? `${dataModel.key}.code`
                : dataModel.key

            if (dataModel.inactive) {
              return { id: dataModel.templateConfigId, key: '', referencePath: null }
            }

            const referencePath = this.#dataModelAutocompleteService.getRefPathFromNamePath(key)

            if (!referencePath) {
              return null
            }

            return { id: dataModel.templateConfigId, key, referencePath }
          })
          .filter(Boolean)

        this.#store.dispatch(
          TemplateConfigActions.updateTemplateConfigKey({
            id: areaId,
            payload,
          })
        )
      }),
      switchMap(() =>
        this.#actions$.pipe(
          ofType(TemplateConfigActions.updateTemplateConfigKeySuccess),
          takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
          take(1),
          tap(() => this.#store.dispatch(ProcessDataModelActions.autoLinkRequested())),
          switchMap(() =>
            this.#actions$.pipe(
              ofType(ProcessDataModelActions.autoLinkSuccess),
              takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
              take(1),
              withLatestFrom(this.#store.select(AiSelectors.selectedDesignerUserTaskAreaId)),
              tap(([, areaId]) =>
                this.#store.dispatch(
                  TemplateConfigActions.loadUserTaskTemplateConfigs({
                    navigationAreaIds: [areaId],
                  })
                )
              ),
              switchMap(() =>
                this.#actions$.pipe(
                  ofType(TemplateConfigActions.loadUserTaskTemplateConfigsSuccess),
                  takeUntil(this.#actions$.pipe(ofType(AiActions.cancelPendingRequest))),
                  take(1),
                  catchError(({ status, error }) =>
                    of(
                      AiDesignerActions.linkTemplateConfigKeysFailure({
                        statusCode: status,
                        errorMsg: error,
                      })
                    )
                  ),
                  map(() => AiDesignerActions.linkTemplateConfigKeysSuccess())
                )
              ),
              catchError(({ status, error }) =>
                of(
                  AiDesignerActions.linkTemplateConfigKeysFailure({
                    statusCode: status,
                    errorMsg: error,
                  })
                )
              )
            )
          )
        )
      )
    )
  )

  onLinkTemplateConfigKeysSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.linkTemplateConfigKeysSuccess),
      map(() => AiDesignerActions.keepDataModelDraftSuccess())
    )
  )

  autoKeepUiDraft$ = createEffect(() =>
    this.#store.select(AiSelectors.pendingAiResponse).pipe(
      withLatestFrom(
        this.#store.select(AiSelectors.selectGeneratedUINew),
        this.#store.select(AiSelectors.selectAutonomyLevel)
      ),
      filter(
        ([pendingAiResponse, generatedUINew, autonomyLevel]) =>
          autonomyLevel === 3 && !!generatedUINew && !pendingAiResponse
      ),
      map(() => AiDesignerActions.keepUiDraft())
    )
  )

  onDiscardUiDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.discardUiDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.generatedUI),
        this.#store.select(AiSelectors.generatedUINew),
        this.#store.select(AiSelectors.editDiffAppliedConfig)
      ),
      mergeMap(([, generatedUI, generatedUINew, editDiffAppliedConfig]) => {
        const discardAction =
          generatedUI || generatedUINew || editDiffAppliedConfig
            ? AiDesignerActions.discardTemplateConfigDraft()
            : AiDesignerActions.discardDataModelDraft()
        const discardActionSuccess =
          generatedUI || generatedUINew || editDiffAppliedConfig
            ? AiDesignerActions.discardUiDraftSuccess()
            : AiDesignerActions.discardDataModelDraftSuccess()
        return [discardAction, discardActionSuccess]
      })
    )
  )

  onStartNewSession = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiActions.startNewSession),
      withLatestFrom(this.#store.select(AiSelectors.generatedUIDataModelChanges)),
      filter(([, generatedUIDataModelChanges]) => !!generatedUIDataModelChanges),
      map(() => AiDesignerActions.discardDataModelDraft())
    )
  )

  discardDataModelDraft = createEffect(() =>
    this.#actions$.pipe(
      ofType(AiDesignerActions.discardDataModelDraft),
      withLatestFrom(
        this.#store.select(AiSelectors.selectedDesignerUserTaskAreaId),
        this.#store.select(AiSelectors.generatedUIDataModelChanges)
      ),
      switchMap(([, areaId, generatedUIDataModelChanges]) => {
        const updateTemplateConfigKeys = [
          ...generatedUIDataModelChanges.existing,
          ...generatedUIDataModelChanges.new,
        ].map(({ templateConfigId }) => ({
          id: templateConfigId,
          key: '',
          referencePath: null,
        }))

        return of(
          ...updateTemplateConfigKeys.map((updateTemplateConfigKey) =>
            TemplateConfigActions.updateTemplateConfigKey({
              id: areaId,
              payload: updateTemplateConfigKey,
            })
          ),
          AiDesignerActions.discardDataModelDraftSuccess()
        )
      })
    )
  )

  sendFeedback = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiDesignerActions.sendFeedback),
        switchMap(({ payload }) => this.#designerApi.sendFeedback(payload))
      ),
    { dispatch: false }
  )

  #mapImagePromptToFormData(
    imageFile: File,
    prompt: string,
    dataModel: BulkDataModelAttribute[],
    sessionId: string
  ): FormData {
    const formData = new FormData()
    formData.append('image_file', imageFile)
    formData.append('prompt', prompt)
    formData.append('data_model', JSON.stringify(dataModel))
    formData.append('session_id', sessionId)
    return formData
  }

  #extractLinkKeyPayloadToUpdate(
    templateConfig: TemplateConfigInterface,
    dataModelKeysToUpdate: string[],
    draftDataModel: DataModelDraft
  ): UpdateTemplateConfigKey[] {
    const hasUpdatingKey = templateConfig.key && dataModelKeysToUpdate.includes(templateConfig.key)
    const updatingKey =
      draftDataModel.existing.find(
        ({ templateConfigMappedKey }) => templateConfigMappedKey === templateConfig.key
      )?.key || draftDataModel.new.find(({ key }) => key === templateConfig.key)?.key

    let currKeyPayload: UpdateTemplateConfigKey[] = []
    if (hasUpdatingKey) {
      const keyRef = this.#dataModelAutocompleteService.getRefPathFromNamePath(updatingKey)

      if (keyRef) {
        currKeyPayload = [{ id: templateConfig.id, key: updatingKey, referencePath: keyRef }]
      }
    }

    const childrenPayloads: UpdateTemplateConfigKey[] =
      _flatten(
        templateConfig.templateConfig?.map((child) =>
          this.#extractLinkKeyPayloadToUpdate(child, dataModelKeysToUpdate, draftDataModel)
        )
      ) ?? []

    return [...currKeyPayload, ...childrenPayloads]
  }
}
